import React, { useState } from 'react';
import { Car, Plane, Clock, Check } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { FareCalculator } from './FareCalculator';

export const Fares = () => {
  const [activeTab, setActiveTab] = useState('airport');

  const fareData = {
    airport: [
      {
        type: 'Sedan',
        price: '₹1,350',
        priceLabel: 'approx',
        icon: Car,
        features: ['Comfortable seating for 4', 'AC vehicle', 'Professional driver', 'Luggage space']
      },
      {
        type: 'SUV',
        price: '₹1,850',
        priceLabel: 'approx',
        icon: Car,
        features: ['Spacious seating for 6-7', 'Premium AC vehicle', 'Professional driver', 'Extra luggage space'],
        popular: true
      }
    ],
    outstation: [
      {
        type: 'Sedan',
        price: '₹14',
        unit: 'per km',
        icon: Car,
        features: ['Comfortable long drives', 'AC vehicle', 'Experienced driver', 'Flexible stops allowed']
      },
      {
        type: 'SUV',
        price: '₹18',
        unit: 'per km',
        icon: Car,
        features: ['Extra comfort for long trips', 'Premium AC vehicle', 'Experienced driver', 'Multiple stops allowed'],
        popular: true
      }
    ],
    rental: [
      {
        type: 'Sedan',
        price: '₹375',
        unit: 'per hour',
        icon: Car,
        features: ['Minimum 4 hours', '15 km/hour included', 'AC vehicle', 'Best for city tours'],
        note: 'Minimum 4 hours booking required'
      },
      {
        type: 'SUV',
        price: '₹500',
        unit: 'per hour',
        icon: Car,
        features: ['Minimum 4 hours', '15 km/hour included', 'Premium AC vehicle', 'Ideal for group tours'],
        note: 'Minimum 4 hours booking required',
        popular: true
      }
    ]
  };

  return (
    <section id="fares" className="py-24 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-block mb-4">
            <span className="inline-flex items-center px-4 py-2 rounded-full bg-[#d4af37]/10 border border-[#d4af37]/30 text-[#d4af37] text-xs font-semibold uppercase tracking-[0.2em]">
              Transparent Pricing
            </span>
          </div>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold text-white mb-4 tracking-tight">
            Taxi Fares in Bangalore — Simple &amp; Affordable
          </h2>
          <p className="text-lg text-gray-400 max-w-2xl mx-auto">
            No hidden charges. What you see is what you pay.
          </p>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 mb-12 h-auto p-2 bg-[#121212] border border-white/10 rounded-full">
            <TabsTrigger 
              value="airport" 
              className="flex items-center justify-center space-x-2 py-4 text-base font-semibold text-gray-400 data-[state=active]:bg-[#d4af37] data-[state=active]:text-black rounded-full transition-all"
            >
              <Plane className="w-5 h-5" />
              <span>Airport</span>
            </TabsTrigger>
            <TabsTrigger 
              value="outstation" 
              className="flex items-center justify-center space-x-2 py-4 text-base font-semibold text-gray-400 data-[state=active]:bg-[#d4af37] data-[state=active]:text-black rounded-full transition-all"
            >
              <Car className="w-5 h-5" />
              <span>Outstation</span>
            </TabsTrigger>
            <TabsTrigger 
              value="rental" 
              className="flex items-center justify-center space-x-2 py-4 text-base font-semibold text-gray-400 data-[state=active]:bg-[#d4af37] data-[state=active]:text-black rounded-full transition-all"
            >
              <Clock className="w-5 h-5" />
              <span>Rental</span>
            </TabsTrigger>
          </TabsList>

          {/* Airport Fares */}
          <TabsContent value="airport" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {fareData.airport.map((fare) => (
                <Card 
                  key={fare.type} 
                  className={`relative lux-card rounded-2xl transition-all duration-300 hover:-translate-y-1 ${
                    fare.popular ? "border-[#d4af37]/60" : ""
                  }`}
                >
                  {fare.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-[#d4af37] text-black px-4 py-1 rounded-full text-xs font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <CardHeader className="text-center pb-4">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${
                      fare.popular ? "bg-[#d4af37]/15 border border-[#d4af37]/40" : "bg-[#1a1a1a] border border-white/10"
                    } flex items-center justify-center shadow-lg`}>
                      <fare.icon className="w-8 h-8 text-[#d4af37]" />
                    </div>
                    <CardTitle className="font-display text-xl font-semibold text-white mb-2">{fare.type}</CardTitle>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-[#d4af37]">{fare.price}</span>
                      <span className="text-gray-400 ml-2">
                        {fare.priceLabel ? `${fare.priceLabel}` : 'per trip'}
                      </span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {fare.features.map((feature) => (
                        <li key={feature} className="flex items-center text-gray-300 text-sm">
                          <Check className="w-5 h-5 text-[#d4af37] mr-3 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Outstation Fares */}
          <TabsContent value="outstation" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {fareData.outstation.map((fare) => (
                <Card 
                  key={fare.type} 
                  className={`relative lux-card rounded-2xl transition-all duration-300 hover:-translate-y-1 ${
                    fare.popular ? "border-[#d4af37]/60" : ""
                  }`}
                >
                  {fare.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-[#d4af37] text-black px-4 py-1 rounded-full text-xs font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <CardHeader className="text-center pb-4">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${
                      fare.popular ? "bg-[#d4af37]/15 border border-[#d4af37]/40" : "bg-[#1a1a1a] border border-white/10"
                    } flex items-center justify-center shadow-lg`}>
                      <fare.icon className="w-8 h-8 text-[#d4af37]" />
                    </div>
                    <CardTitle className="font-display text-xl font-semibold text-white mb-2">{fare.type}</CardTitle>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-[#d4af37]">{fare.price}</span>
                      <span className="text-gray-400 ml-2">{fare.unit}</span>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {fare.features.map((feature) => (
                        <li key={feature} className="flex items-center text-gray-300 text-sm">
                          <Check className="w-5 h-5 text-[#d4af37] mr-3 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Disclaimer for Outstation */}
            <div className="mt-8 max-w-5xl mx-auto">
              <div className="bg-[#d4af37]/5 border-l-4 border-[#d4af37] rounded-lg p-4">
                <p className="text-sm text-gray-300">
                  <span className="font-semibold text-[#d4af37]">No hidden charges</span> - Price excludes Toll, Parking, Driver allowance and Permit (if applicable)
                </p>
              </div>
            </div>
          </TabsContent>

          {/* Rental Fares */}
          <TabsContent value="rental" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {fareData.rental.map((fare) => (
                <Card 
                  key={fare.type} 
                  className={`relative lux-card rounded-2xl transition-all duration-300 hover:-translate-y-1 ${
                    fare.popular ? "border-[#d4af37]/60" : ""
                  }`}
                >
                  {fare.popular && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                      <span className="bg-[#d4af37] text-black px-4 py-1 rounded-full text-xs font-semibold">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <CardHeader className="text-center pb-4">
                    <div className={`w-16 h-16 mx-auto mb-4 rounded-2xl ${
                      fare.popular ? "bg-[#d4af37]/15 border border-[#d4af37]/40" : "bg-[#1a1a1a] border border-white/10"
                    } flex items-center justify-center shadow-lg`}>
                      <fare.icon className="w-8 h-8 text-[#d4af37]" />
                    </div>
                    <CardTitle className="font-display text-xl font-semibold text-white mb-2">{fare.type}</CardTitle>
                    <div className="flex items-baseline justify-center">
                      <span className="text-4xl font-bold text-[#d4af37]">{fare.price}</span>
                      <span className="text-gray-400 ml-2">{fare.unit}</span>
                    </div>
                    {fare.note && (
                      <p className="text-sm text-gray-500 mt-2">{fare.note}</p>
                    )}
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3">
                      {fare.features.map((feature) => (
                        <li key={feature} className="flex items-center text-gray-300 text-sm">
                          <Check className="w-5 h-5 text-[#d4af37] mr-3 flex-shrink-0" />
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
            
            {/* Disclaimer for Rental */}
            <div className="mt-8 max-w-5xl mx-auto">
              <div className="bg-[#d4af37]/5 border-l-4 border-[#d4af37] rounded-lg p-4">
                <p className="text-sm text-gray-300">
                  <span className="font-semibold text-[#d4af37]">No hidden charges</span> - Price excludes Toll, Parking, Driver allowance and Permit (if applicable)
                </p>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        {/* Fare Calculator */}
        <FareCalculator />

        {/* Additional Info */}
        <div className="mt-16 max-w-4xl mx-auto">
          <Card className="lux-card rounded-2xl">
            <CardContent className="p-8 text-center">
              <h3 className="font-display text-2xl font-semibold text-white mb-4">Need a Custom Quote?</h3>
              <p className="text-gray-400 mb-6">
                Planning a special trip or need a customized package? Call us for personalized pricing and special offers.
              </p>
              <a href="tel:+917507867648">
                <button className="inline-flex items-center px-8 py-4 rounded-full bg-[#d4af37] hover:bg-[#c9a35c] text-black font-semibold transition-colors">
                  Call +91 75078 67648 for Custom Quotes
                </button>
              </a>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>
  );
};
